import React, { useEffect, useRef, useState } from 'react';
import DrQuackMascot, { DuckTypingDots } from './DrQuackMascot';
import './FloatingChatWidget.css';
import { API, authFetch } from '../api';
import ReactMarkdown from 'react-markdown';

const SUGGESTED_PROMPTS = [
  'What does my HRV score mean?',
  'How can I connect to a sensor?',
  'How can I recover faster?',
  'Should I train hard today?',
];

const INITIAL_MESSAGES = [
  {
    id: 'greeting',
    role: 'assistant',
    text: "Hi! I'm your AI companion 🦆 Ask me about your HRV, sleep, or recovery — I'll do my best to explain.",
  },
];

/**
 * Placeholder for the real AI endpoint. Mirrors the contract used in
 * mobile/app/(tabs)/ai-chat.tsx so when the backend lands the same
 * request shape works on both clients.
 *
 * Suggested real signature (coordinate with whoever wires up the AI):
 *   POST /api/ai/chat
 *   body: { history: Message[], message: string }
 *   resp: { reply: string }
 */

/**
 * Floating chat widget — Intercom/Crisp-style. A sticky bubble pinned to
 * the bottom-right corner; clicking it expands a chat panel anchored to
 * the same corner.
 *
 * Mount once at the app shell level (ProtectedRoute) so the widget
 * follows the user across every authenticated page. NOT shown on the
 * landing or register pages — Dr. Quack greets the user only after they
 * have an account.
 */
export default function FloatingChatWidget() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState(INITIAL_MESSAGES);
  const [isThinking, setIsThinking] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // 2. Move the API function INSIDE the component and remove TS annotations
  const sendMessageToAPI = async (text, history) => {
    const response = await authFetch(
        API.chatbot(),
        {
          method: 'POST',
          body: JSON.stringify({ history: history }),
        },
    );

    if (!response.ok) {
      throw new Error('Network response was not ok');
    }

    const data = await response.json();
    return data.reply;
  };

  // Auto-scroll the message list whenever new content arrives or the
  // thinking indicator toggles. The 80ms delay gives the new bubble time
  // to mount before we scroll to it.
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }
    }, 80);
    return () => clearTimeout(t);
  }, [messages, isThinking, open]);

  // Focus the input shortly after opening — better keyboard UX. Slight
  // delay so the slide-in animation can finish first.
  useEffect(() => {
    if (open && inputRef.current) {
      const t = setTimeout(() => inputRef.current?.focus(), 180);
      return () => clearTimeout(t);
    }
  }, [open]);

  const sendMessage = async (text) => {
    const trimmed = text.trim();
    if (!trimmed || isThinking) return;

    const userMsg = {
      id: `u-${Date.now()}`,
      role: 'user',
      text: trimmed,
    };

    // Render the user's message immediately and clear the input so the
    // UI feels responsive even before the backend replies.
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setIsThinking(true);

    try {
      const reply = await sendMessageToAPI(trimmed, [...messages, userMsg]);
      setMessages((prev) => [
        ...prev,
        { id: `d-${Date.now()}`, role: 'assistant', text: reply },
      ]);
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          id: `d-err-${Date.now()}`,
          role: 'assistant',
          text: "Sorry, I couldn't reach my brain just now. Try again in a moment?",
        },
      ]);
    } finally {
      setIsThinking(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  const showSuggestions = messages.length === 1 && !isThinking;

  return (
    <>
      {/* Floating bubble — always present so the user can pop the panel
          open / closed. Uses aria-expanded so screen readers know the
          state. */}
      <button
        type="button"
        className={`fcw-launcher ${open ? 'is-open' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? 'Close chat with Dr. Quack' : 'Open chat with Dr. Quack'}
        aria-expanded={open}
      >
        {open ? (
          <svg
            className="fcw-launcher-close"
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        ) : (
          <DrQuackMascot size={48} animate />
        )}
      </button>

      {/* Chat panel — anchored to the same corner. We render it
          conditionally so the input element doesn't sit invisibly in the
          DOM eating focus / screen-reader attention when closed. */}
      {open && (
        <div className="fcw-panel" role="dialog" aria-label="Chat with Dr. Quack">
          {/* Header — duck + status */}
          <div className="fcw-header">
            <div className="fcw-header-avatar">
              <DrQuackMascot size={36} animate={false} />
            </div>
            <div className="fcw-header-text">
              <div className="fcw-header-name">Dr. Quack</div>
              <div className="fcw-header-status">
                <span
                  className={`fcw-status-dot ${isThinking ? 'is-thinking' : ''}`}
                />
                <span>{isThinking ? 'Thinking…' : 'Online'}</span>
              </div>
            </div>
            <button
              type="button"
              className="fcw-header-close"
              onClick={() => setOpen(false)}
              aria-label="Close"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          {/* Message list */}
          <div className="fcw-messages" ref={scrollRef}>
            {messages.map((msg) => (
              <MessageRow key={msg.id} message={msg} />
            ))}
            {isThinking && (
              <div className="fcw-msg-row fcw-msg-row--duck">
                <div className="fcw-msg-avatar">
                  <DrQuackMascot size={28} animate={false} />
                </div>
                <div className="fcw-bubble fcw-bubble--duck fcw-bubble--thinking">
                  <DuckTypingDots color="#5CB89A" size={6} />
                </div>
              </div>
            )}
          </div>

          {/* Suggested prompts — only on the first turn. After the user
              has actually sent something, the chips disappear so they
              don't clutter the conversation. */}
          {showSuggestions && (
            <div className="fcw-suggestions">
              <div className="fcw-suggestions-label">Try asking…</div>
              <div className="fcw-suggestions-row">
                {SUGGESTED_PROMPTS.map((prompt) => (
                  <button
                    key={prompt}
                    type="button"
                    className="fcw-suggestion-chip"
                    onClick={() => sendMessage(prompt)}
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input bar */}
          <form className="fcw-input-bar" onSubmit={handleSubmit}>
            <input
              ref={inputRef}
              type="text"
              className="fcw-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask Dr. Quack…"
              disabled={isThinking}
              maxLength={1000}
            />
            <button
              type="submit"
              className="fcw-send-btn"
              disabled={!input.trim() || isThinking}
              aria-label="Send"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="12" y1="19" x2="12" y2="5" />
                <polyline points="5 12 12 5 19 12" />
              </svg>
            </button>
          </form>
        </div>
      )}
    </>
  );
}

function MessageRow({ message }) {
  const isUser = message.role === 'user';
  return (
    <div className={`fcw-msg-row ${isUser ? 'fcw-msg-row--user' : 'fcw-msg-row--duck'}`}>
      {!isUser && (
        <div className="fcw-msg-avatar">
          <DrQuackMascot size={28} animate={false} />
        </div>
      )}
      <div className={`fcw-bubble ${isUser ? 'fcw-bubble--user' : 'fcw-bubble--duck'}`}>
        {!isUser ? (
            <ReactMarkdown>{message.text}</ReactMarkdown>
        ) : (
            message.text
        )}
      </div>
    </div>
  );
}